import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Pencil,
  Target,
  FolderOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";
import { GoalForm } from "./GoalForm";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CaseRecord {
  id: number;
  person_id: number;
  agency_id: number;
  category_id: number | null;
  description: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string | null;
  created_by: number | null;
  created_at: string;
  category_name?: string;
  author_first_name?: string;
  author_last_name?: string;
}

export interface Goal {
  id: number;
  case_id: number;
  goal_text: string;
  target_date: string | null;
  completed_date: string | null;
  status: string | null;
  outcome: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const statusColors: Record<string, string> = {
  open: "bg-green-100 text-green-800",
  in_progress: "bg-blue-100 text-blue-800",
  closed: "bg-gray-100 text-gray-800",
  on_hold: "bg-amber-100 text-amber-800",
};

const goalStatusColors: Record<string, string> = {
  pending: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-700",
  achieved: "bg-green-100 text-green-700",
  not_achieved: "bg-red-100 text-red-700",
  deferred: "bg-amber-100 text-amber-700",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CasesListProps {
  personId: string;
}

export function CasesList({ personId }: CasesListProps) {
  const queryClient = useQueryClient();
  const [expandedCaseIds, setExpandedCaseIds] = useState<Set<number>>(
    new Set(),
  );
  const [showCaseForm, setShowCaseForm] = useState(false);
  const [goalFormTarget, setGoalFormTarget] = useState<{
    caseId: number;
    goal?: Goal;
  } | null>(null);

  const { data: casesData, isLoading } = useQuery<{ data: CaseRecord[] }>({
    queryKey: ["cases", personId],
    queryFn: () => api.get<{ data: CaseRecord[] }>(`/clients/${personId}/cases`),
    enabled: !!personId,
  });

  const createCaseMutation = useMutation({
    mutationFn: (data: { description: string; status: string }) =>
      api.post(`/clients/${personId}/cases`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases", personId] });
      setShowCaseForm(false);
    },
  });

  function toggleCase(caseId: number) {
    setExpandedCaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  }

  const cases = casesData?.data ?? [];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Case Management</h2>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => setShowCaseForm(true)}
        >
          <Plus className="h-4 w-4" />
          Add Case
        </Button>
      </div>

      {/* New case inline form */}
      {showCaseForm && (
        <Card>
          <CardContent className="p-4">
            <NewCaseInlineForm
              onSubmit={(data) => createCaseMutation.mutate(data)}
              onCancel={() => setShowCaseForm(false)}
              isLoading={createCaseMutation.isPending}
              error={createCaseMutation.isError ? ((createCaseMutation.error as Error)?.message ?? "Failed to create case") : undefined}
            />
          </CardContent>
        </Card>
      )}

      {/* Goal form modal */}
      {goalFormTarget && (
        <GoalForm
          personId={personId}
          caseId={goalFormTarget.caseId}
          goal={goalFormTarget.goal}
          onClose={() => setGoalFormTarget(null)}
        />
      )}

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && cases.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FolderOpen className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No cases found.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Cases list */}
      {!isLoading &&
        cases.map((caseRecord) => (
          <CaseCard
            key={caseRecord.id}
            personId={personId}
            caseRecord={caseRecord}
            isExpanded={expandedCaseIds.has(caseRecord.id)}
            onToggle={() => toggleCase(caseRecord.id)}
            onAddGoal={() =>
              setGoalFormTarget({ caseId: caseRecord.id })
            }
            onEditGoal={(goal) =>
              setGoalFormTarget({ caseId: caseRecord.id, goal })
            }
          />
        ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case Card with goals
// ---------------------------------------------------------------------------

interface CaseCardProps {
  personId: string;
  caseRecord: CaseRecord;
  isExpanded: boolean;
  onToggle: () => void;
  onAddGoal: () => void;
  onEditGoal: (goal: Goal) => void;
}

function CaseCard({
  personId,
  caseRecord,
  isExpanded,
  onToggle,
  onAddGoal,
  onEditGoal,
}: CaseCardProps) {
  const { data: goalsData } = useQuery<{ data: Goal[] }>({
    queryKey: ["goals", caseRecord.id],
    queryFn: () =>
      api.get<{ data: Goal[] }>(
        `/clients/${personId}/cases/${caseRecord.id}/goals`,
      ),
    enabled: isExpanded,
  });

  const goals = goalsData?.data ?? [];
  const authorName =
    caseRecord.author_first_name && caseRecord.author_last_name
      ? `${caseRecord.author_first_name} ${caseRecord.author_last_name}`
      : null;

  return (
    <Card>
      <CardHeader
        className="cursor-pointer pb-2"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <div>
              <CardTitle className="text-sm font-medium">
                {caseRecord.category_name ?? "General Case"}
                {caseRecord.description && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    - {caseRecord.description}
                  </span>
                )}
              </CardTitle>
              <CardDescription className="mt-0.5">
                {caseRecord.start_date &&
                  new Date(caseRecord.start_date).toLocaleDateString()}
                {caseRecord.end_date &&
                  ` - ${new Date(caseRecord.end_date).toLocaleDateString()}`}
                {authorName && ` | ${authorName}`}
              </CardDescription>
            </div>
          </div>
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize",
              statusColors[caseRecord.status ?? "open"] ??
                statusColors.open,
            )}
          >
            {(caseRecord.status ?? "open").replace("_", " ")}
          </span>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-3">
            {/* Goals header */}
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Target className="h-4 w-4" />
                Goals ({goals.length})
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddGoal();
                }}
              >
                <Plus className="h-3 w-3" />
                Add Goal
              </Button>
            </div>

            {/* Goals list */}
            {goals.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No goals set for this case.
              </p>
            ) : (
              <div className="space-y-2">
                {goals.map((goal) => (
                  <div
                    key={goal.id}
                    className="flex items-start justify-between rounded-md border p-3"
                  >
                    <div className="space-y-1">
                      <p className="text-sm font-medium">{goal.goal_text}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        {goal.target_date && (
                          <span>
                            Target:{" "}
                            {new Date(goal.target_date).toLocaleDateString()}
                          </span>
                        )}
                        {goal.completed_date && (
                          <span>
                            Completed:{" "}
                            {new Date(
                              goal.completed_date,
                            ).toLocaleDateString()}
                          </span>
                        )}
                        {goal.outcome && (
                          <span>Outcome: {goal.outcome}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                          goalStatusColors[goal.status ?? "pending"] ??
                            goalStatusColors.pending,
                        )}
                      >
                        {(goal.status ?? "pending").replace("_", " ")}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditGoal(goal);
                        }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Inline new case form
// ---------------------------------------------------------------------------

interface NewCaseInlineFormProps {
  onSubmit: (data: { description: string; status: string; startDate?: string }) => void;
  onCancel: () => void;
  isLoading: boolean;
  error?: string;
}

function NewCaseInlineForm({
  onSubmit,
  onCancel,
  isLoading,
  error,
}: NewCaseInlineFormProps) {
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("open");
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">New Case</h3>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Case description..."
          className="flex w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="on_hold">On Hold</option>
          </select>
        </div>
      </div>
      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => onSubmit({ description, status, startDate })}
          disabled={!description.trim()}
          loading={isLoading}
        >
          Create Case
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
